import React, { useState } from 'react';
import { User, Lock, X, Save, AlertCircle } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

const ProfileModal = ({ userProfile, initialTab = 'data', onClose, onSuccess }) => {
    const [tab, setTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        name: userProfile?.name || '',
        username: userProfile?.username || '',
        email: userProfile?.email || '',
        whatsapp: userProfile?.userWhatsappNumber || ''
    });

    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handleUpdateData = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put('/auth/me', {
                name: formData.name,
                username: formData.username,
                email: formData.email,
                userWhatsappNumber: formData.whatsapp
            });
            toast.success("Dados atualizados com sucesso!");
            onSuccess(); // Refresh profile in parent
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao atualizar perfil.");
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error("A nova senha e a confirmação não coincidem.");
        }
        if (passwordData.newPassword.length < 6) {
            return toast.error("A nova senha deve ter pelo menos 6 caracteres.");
        }

        setLoading(true);
        try {
            await api.post('/auth/change-password', {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            });
            toast.success("Senha alterada com sucesso!");
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao alterar senha.");
        } finally {
            setLoading(false);
        }
    };

    // STYLES
    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
    };

    const modalStyle = {
        background: '#fff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
    };

    const headerStyle = {
        padding: '20px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8fafc'
    };

    const titleStyle = { margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1e293b' };

    const tabContainerStyle = {
        display: 'flex',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#fff'
    };

    const tabStyle = (active) => ({
        flex: 1,
        padding: '15px 0',
        textAlign: 'center',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid #ee4d2d' : '2px solid transparent',
        color: active ? '#ee4d2d' : '#64748b',
        fontWeight: active ? 'bold' : '500',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        transition: '0.2s'
    });

    const contentStyle = { padding: '24px' };

    const inputGroupStyle = { marginBottom: '16px' };
    const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#475569', marginBottom: '6px' };
    const inputStyle = {
        width: '100%', padding: '10px 12px', borderRadius: '8px',
        border: '1px solid #cbd5e1', fontSize: '14px', color: '#334155',
        boxSizing: 'border-box'
    };
    
    const emailWrapperStyle = {
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
    };

    const verifyBtnStyle = {
        padding: '0 12px',
        height: '40px',
        background: '#f1f5f9',
        border: '1px solid #cbd5e1',
        borderRadius: '8px',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: 'not-allowed', // Em breve
        whiteSpace: 'nowrap'
    };

    const submitBtnStyle = {
        width: '100%',
        padding: '12px',
        background: '#ee4d2d',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        marginTop: '10px',
        transition: '0.2s'
    };

    const infoBoxStyle = {
        backgroundColor: '#fffbeb',
        border: '1px solid #fef3c7',
        borderLeft: '4px solid #f59e0b',
        padding: '12px',
        borderRadius: '6px',
        display: 'flex',
        gap: '12px',
        marginBottom: '20px'
    };

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <div style={headerStyle}>
                    <h2 style={titleStyle}>Minha Conta</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                        <X size={20} />
                    </button>
                </div>

                <div style={tabContainerStyle}>
                    <button style={tabStyle(tab === 'data')} onClick={() => setTab('data')}>
                        <User size={16} /> Meus Dados
                    </button>
                    <button style={tabStyle(tab === 'password')} onClick={() => setTab('password')}>
                        <Lock size={16} /> Senha
                    </button>
                </div>

                <div style={contentStyle}>
                    {tab === 'data' ? (
                        <form onSubmit={handleUpdateData}>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Nome Completo</label>
                                <input
                                    type="text"
                                    style={inputStyle}
                                    placeholder="Ex: João Silva"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Nome de Usuário</label>
                                <input
                                    type="text"
                                    style={inputStyle}
                                    placeholder="Ex: joaosilva99"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Email</label>
                                <div style={emailWrapperStyle}>
                                    <input
                                        type="email"
                                        style={inputStyle}
                                        placeholder="seu@email.com"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                    <button type="button" style={verifyBtnStyle} title="Em breve!">
                                        Confirmar Email
                                    </button>
                                </div>
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>WhatsApp (com DDD)</label>
                                <div style={emailWrapperStyle}>
                                    <input
                                        type="text"
                                        style={inputStyle}
                                        placeholder="Ex: 11999999999"
                                        value={formData.whatsapp}
                                        onChange={e => setFormData({ ...formData, whatsapp: e.target.value })}
                                        required
                                    />
                                    {/* A verificação é feita na tela de Amostra Grátis, não precisamos do botão aqui, 
                                        mas se ele trocar, perderá o selo de verificado no backend. */}
                                </div>
                                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                    Atenção: Ao alterar o número, sua verificação do WhatsApp (se houver) será removida por segurança.
                                </p>
                            </div>

                            <button type="submit" style={submitBtnStyle} disabled={loading}>
                                <Save size={18} /> {loading ? 'Salvando...' : 'Salvar Alterações'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleChangePassword}>
                            <div style={infoBoxStyle}>
                                <AlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0 }} />
                                <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.4' }}>
                                    Escolha uma senha forte. Sua sessão não será desconectada ao alterar a senha, mas novos logins exigirão a nova senha.
                                </p>
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Senha Atual</label>
                                <input
                                    type="password"
                                    style={inputStyle}
                                    placeholder="Digite sua senha atual"
                                    value={passwordData.oldPassword}
                                    onChange={e => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Nova Senha</label>
                                <input
                                    type="password"
                                    style={inputStyle}
                                    placeholder="Mínimo 6 caracteres"
                                    value={passwordData.newPassword}
                                    onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    required
                                    minLength="6"
                                />
                            </div>

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Confirmar Nova Senha</label>
                                <input
                                    type="password"
                                    style={inputStyle}
                                    placeholder="Repita a nova senha"
                                    value={passwordData.confirmPassword}
                                    onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    required
                                    minLength="6"
                                />
                            </div>

                            <button type="submit" style={submitBtnStyle} disabled={loading}>
                                <Lock size={18} /> {loading ? 'Alterando...' : 'Alterar Senha'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;
